import { replaceTokenAtCursor } from "./macro-token-utils"
import { setCaret } from "./macro-caret-utils"
import { getContentEditableCursor } from "./macro-editable-target"
import {
    clampIndex,
    isTokenChar,
    replaceRange,
    resolveCursorIndex,
    resolveTokenContextInText,
    resolveTokenReplacementRange,
} from "./macro-token-helpers"
import {
    applyLocalContentEditableReplacement,
    resolveLocalContentEditableReplacement,
} from "./macro-selection-snapshot"
import { createChipElement } from "./macro-chip-dom"

export function insertTokenIntoInput(element, token, context, cursorOverride) {
    const value = element.value ?? ""
    const cursorIndex = resolveCursorIndex(element, cursorOverride, value.length)
    const match = resolveTokenReplacementRange(value, cursorIndex, context)
    if (match) {
        const nextValue = replaceRange(value, match.start, match.end, token)
        const nextCursor = match.start + token.length
        element.value = nextValue
        element.setSelectionRange(nextCursor, nextCursor)
        element.dispatchEvent(new Event("input", { bubbles: true }))
        return true
    }

    const nextValue = replaceTokenAtCursor(value, cursorIndex, token)
    const nextCursor = cursorIndex + token.length
    element.value = nextValue
    element.setSelectionRange(nextCursor, nextCursor)
    element.dispatchEvent(new Event("input", { bubbles: true }))
    return true
}

export function insertTokenIntoContentEditable(element, token, snapshot) {
    const selection = window.getSelection()
    if (!selection || selection.rangeCount === 0) return false
    const range = selection.getRangeAt(0)
    if (!element.contains(range.startContainer)) return false

    const localReplacement = resolveLocalContentEditableReplacement(range, snapshot)
    if (localReplacement) {
        applyLocalContentEditableReplacement(range, localReplacement, token)
        element.dispatchEvent(new Event("input", { bubbles: true }))
        return true
    }

    const fullText = snapshot?.fullText ?? element.textContent ?? ""
    const fullCursorIndex = snapshot?.cursorIndex ?? getContentEditableCursor(element)
    const fullContext = snapshot?.context ?? resolveTokenContextInText(fullText, fullCursorIndex)
    const fullReplacement = resolveTokenReplacementRange(fullText, fullCursorIndex, fullContext)
    if (fullReplacement) {
        const replaced = replaceContentEditableRange(element, fullReplacement, token)
        if (replaced) return true
    }

    if (!range.collapsed) {
        range.deleteContents()
    }

    let node = range.startContainer
    let offset = range.startOffset

    if (node.nodeType !== Node.TEXT_NODE) {
        const textNode = document.createTextNode("")
        range.insertNode(textNode)
        node = textNode
        offset = 0
    }

    const text = node.nodeValue ?? ""
    const match = resolveTokenReplacementRange(text, offset, resolveTokenContextInText(text, offset))
    if (match) {
        node.nodeValue = `${text.slice(0, match.start)}${token}${text.slice(match.end)}`
        setCaret(node, match.start + token.length)
    } else {
        const context = resolveTokenContextInText(text, offset)
        if (context) {
            node.nodeValue = `${text.slice(0, context.start)}${token}${text.slice(context.end)}`
            setCaret(node, context.start + token.length)
        } else {
            node.nodeValue = `${text.slice(0, offset)}${token}${text.slice(offset)}`
            setCaret(node, offset + token.length)
        }
    }
    element.dispatchEvent(new Event("input", { bubbles: true }))
    return true
}

export function insertChipIntoContentEditable(element, chipHtml, snapshot) {
    const selection = window.getSelection()
    if (!selection || selection.rangeCount === 0) return false
    const range = selection.getRangeAt(0)
    if (!element.contains(range.startContainer)) return false

    const localReplacement = resolveLocalContentEditableReplacement(range, snapshot)
    if (localReplacement) {
        const { node, start, end } = localReplacement
        const text = node.nodeValue ?? ""
        node.nodeValue = text.slice(0, start) + text.slice(end)
        const insertRange = document.createRange()
        insertRange.setStart(node, start)
        insertRange.collapse(true)
        selection.removeAllRanges()
        selection.addRange(insertRange)
    } else if (!range.collapsed) {
        range.collapse(true)
        selection.removeAllRanges()
        selection.addRange(range)
    }

    // Use direct DOM insertion instead of document.execCommand("insertHTML")
    // to avoid browser quirks that merge adjacent paragraphs
    const chipNode = createChipElement(chipHtml)
    if (!chipNode) return false

    const currentRange = selection.getRangeAt(0)
    // Store last child before insertNode — DocumentFragment empties after insertion
    const lastInserted = chipNode.lastChild
    currentRange.insertNode(chipNode)

    // Move cursor to just after the last inserted node (chip + separator space)
    const afterRange = document.createRange()
    afterRange.setStartAfter(lastInserted)
    afterRange.collapse(true)
    selection.removeAllRanges()
    selection.addRange(afterRange)

    element.dispatchEvent(new Event("input", { bubbles: true }))
    return true
}

/**
 * Last-resort fallback for component token insertion when the stored selection
 * is stale (e.g. Jodit replaced DOM nodes while the dialog was open).
 * Walks the text nodes looking for a partial "$cmp:" token and replaces it.
 */
export function replacePartialCmpToken(element, chipHtml, tokenFallback) {
    const walker = document.createTreeWalker(element, NodeFilter.SHOW_TEXT, null)
    let node = walker.nextNode()
    while (node) {
        const text = node.nodeValue ?? ""
        const idx = text.indexOf("$cmp:")
        if (idx !== -1) {
            // Find the end of the partial token (up to next $ or whitespace)
            let end = idx + 5
            while (end < text.length && isTokenChar(text[end])) end++
            if (text[end] === "$") end++ // include closing $

            // Remove the partial token text and position cursor
            node.nodeValue = text.slice(0, idx) + text.slice(end)
            const range = document.createRange()
            range.setStart(node, idx)
            range.collapse(true)
            const selection = window.getSelection()
            if (selection) {
                selection.removeAllRanges()
                selection.addRange(range)
            }

            // Use direct DOM insertion instead of execCommand
            const chipNode = createChipElement(chipHtml)
            if (chipNode) {
                // Store last child before insertNode — DocumentFragment empties after insertion
                const lastInserted = chipNode.lastChild
                range.insertNode(chipNode)
                const afterRange = document.createRange()
                afterRange.setStartAfter(lastInserted)
                afterRange.collapse(true)
                if (selection) {
                    selection.removeAllRanges()
                    selection.addRange(afterRange)
                }
            } else {
                node.nodeValue = text.slice(0, idx) + tokenFallback + text.slice(end)
                setCaret(node, idx + tokenFallback.length)
            }
            element.dispatchEvent(new Event("input", { bubbles: true }))
            return true
        }
        node = walker.nextNode()
    }
    return false
}

export function replaceContentEditableRange(element, replacement, token) {
    if (!element || !replacement) return false
    const startPoint = findTextNodeAtIndex(element, replacement.start)
    const endPoint = findTextNodeAtIndex(element, replacement.end)
    if (!startPoint || !endPoint) return false
    const range = document.createRange()
    range.setStart(startPoint.node, startPoint.offset)
    range.setEnd(endPoint.node, endPoint.offset)
    range.deleteContents()
    const textNode = document.createTextNode(token)
    range.insertNode(textNode)
    setCaret(textNode, token.length)
    element.dispatchEvent(new Event("input", { bubbles: true }))
    return true
}

export function findTextNodeAtIndex(element, index) {
    const walker = document.createTreeWalker(element, NodeFilter.SHOW_TEXT, null)
    let current = walker.nextNode()
    let remaining = clampIndex(index ?? 0, element.textContent?.length ?? 0)
    let lastNode = null
    while (current) {
        const text = current.nodeValue ?? ""
        lastNode = current
        if (remaining <= text.length) {
            return { node: current, offset: remaining }
        }
        remaining -= text.length
        current = walker.nextNode()
    }
    if (lastNode) {
        return { node: lastNode, offset: lastNode.nodeValue?.length ?? 0 }
    }
    return null
}
